<template>
    <div class="flex flex-column xl:flex-row lg:flex-row md:flex-row px-3 py-2">
        <div class="flex flex-row align-items-center">
            <SelectButton v-model="selectedColumns" :options="columns" optionLabel="name" multiple aria-labelledby="multiple">
                <template #option="slotProps">
                    <span class="text-xs py-1 white-space-nowrap">{{ slotProps.option.name }}</span>
                </template>
            </SelectButton>
            <Button label="Добавить" icon="pi pi-plus" size="small" @click="openAddDialog()" class="border-round-lg border-0 ml-2" severity="success" />
            <Button label="Обновить" icon="pi pi-sync" size="small" @click="LoadRecipientsBlacklist" class="border-round-lg border-0 ml-2" severity="info" outlined/>
            <Button label="Сбросить" icon="pi pi-filter" size="small" @click="resetFilter" class="border-round-lg border-0 ml-2" severity="info" outlined/>
        </div>
    </div>

    <DataTable
        v-if="list"
        :value="list"
        lazy
        paginator
        :rows="20"
        v-model:filters="filters"
        ref="dt"
        dataKey="id"
        :totalRecords="totalRecords"
        :loading="loading"
        @page="onPage($event)"
        @sort="onSort($event)"
        @filter="onFilter($event)"
        filterDisplay="menu"
        :globalFilterFields="[
            'id',
            '_id',
            'recipient_id',
            'created_by',
            'fingerprint',
            'reason',
            'source',
            'payload.currency_code',
            'payload.bank_code',
            'payload.payment_system_code',
            'payload.fields.fullname',
            'payload.fields.phone_number',
            'payload.fields.card_number',
            'payload.fields.account_number',
            'payload.fields.wallet_address',
            'payload.fields.wallet_network',
            'payload.fields.telegram',
            'payload.fields.bank_name',
            'payload.fields.address',
            'payload.fields.city',
            'payload.fields.country',
            'payload.fields.swift_code',
            'payload.fields.iban_number',
            'payload.fields.upi_id',
            'payload.fields.wallet_id'
        ]"
        :rowsPerPageOptions="[5, 10, 20, 50, 100, 200, 300, 400, 500]"
    >

        <Column field="_id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">ID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText
                    type="text"
                    v-model="filterModel.value"
                    @keydown.enter="filterCallback()"
                    class="p-column-filter w-12rem"
                    placeholder="Найти"
                />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 align-items-center" v-if="data._id">
                    <span class="text-xs flex my-auto overflow-hidden" style="max-width:4em">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data._id }}
                        </span>
                    </span>...
                    <Button
                        icon="pi pi-copy text-xs"
                        @click="copy(data._id)"
                        size="small"
                        class="border-round-md border-round-lg border-0"
                        severity="info"
                        outlined
                    />
                </div>
            </template>
        </Column>

        <Column field="recipient_id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">Recipient ID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 align-items-center" v-if="data.recipient_id">
                    <span class="text-xs flex my-auto overflow-hidden" style="max-width:4em">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.recipient_id }}
                        </span>
                    </span>...
                    <Button
                        icon="pi pi-copy text-xs"
                        @click="copy(data.recipient_id)"
                        size="small"
                        class="border-round-md border-round-lg border-0"
                        severity="info"
                        outlined
                    />
                    <Button
                        icon="pi pi-external-link text-xs"
                        @click="openRecipientsById(data.recipient_id)"
                        size="small"
                        class="border-round-md border-round-lg border-0"
                        severity="info"
                        outlined
                    />
                </div>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="fingerprint" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">Fingerprint</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-14rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 align-items-center" v-if="data.fingerprint">
                    <span class="text-xs flex my-auto overflow-hidden" style="max-width:8em">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.fingerprint }}
                        </span>
                    </span>...
                    <Button icon="pi pi-copy text-xs" @click="copy(data.fingerprint)" size="small" class="border-round-md border-round-lg border-0" severity="info" outlined />
                </div>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="reason" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">Причина</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.reason">{{ data.reason }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column :exportable="false">
            <template #header>
                <span class="text-xs">Действия</span>
            </template>
            <template #body="{ data }">
                <Button
                    icon="pi pi-pencil"
                    size="small"
                    class="border-round-md border-round-lg border-0"
                    severity="info"
                    outlined
                    @click="openEditDialog(data)"
                />
            </template>
        </Column>

        <Column field="source" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">Источник</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.source">{{ data.source }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="active">
            <template #header>
                <span class="text-xs">Активен</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <Dropdown
                    v-model="filterModel.value"
                    @change="filterCallback()"
                    :options="[
                        { label: 'Все', value: null },
                        { label: 'Да', value: true },
                        { label: 'Нет', value: false }
                    ]"
                    optionLabel="label"
                    optionValue="value"
                    class="p-column-filter w-10rem"
                />
            </template>
            <template #body="{ data }">
                <span
                    class="text-xs px-2 py-1 border-round-md"
                    :class="data.active ? 'bg-green-400 text-white' : 'surface-200 text-600'"
                >
                    {{ data.active ? 'Да' : 'Нет' }}
                </span>
            </template>
        </Column>

        <Column field="created_by" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">Создал</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 align-items-center" v-if="data.created_by">
                    <span class="text-xs flex my-auto overflow-hidden" style="max-width:4em">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.created_by }}
                        </span>
                    </span>...
                    <Button
                        icon="pi pi-copy text-xs"
                        @click="copy(data.created_by)"
                        size="small"
                        class="border-round-md border-round-lg border-0"
                        severity="info"
                        outlined
                    />
                    <Button
                        icon="pi pi-external-link text-xs"
                        @click="detail_page(data.created_by, 'user', true)"
                        size="small"
                        class="border-round-md border-round-lg border-0"
                        severity="info"
                        outlined
                    />
                </div>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="created_at" sortable filterField="created_at" dataType="date" :showFilterMatchModes="false" :showFilterOperator="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">Создан</span>
            </template>
            <template #filter="{ filterModel }">
                <Calendar v-model="filterModel.value" dateFormat="dd.mm.yy" placeholder="dd.mm.yy" mask="99.99.9999" />
            </template>
            <template #body="{ data }">
                <small class="text-xs white-space-nowrap">{{ formatDate(data.created_at) }}</small>
            </template>
        </Column>

        <Column field="updated_at" sortable filterField="updated_at" dataType="date" :showFilterMatchModes="false" :showFilterOperator="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">Обновлен</span>
            </template>
            <template #filter="{ filterModel }">
                <Calendar v-model="filterModel.value" dateFormat="dd.mm.yy" placeholder="dd.mm.yy" mask="99.99.9999" />
            </template>
            <template #body="{ data }">
                <small class="text-xs white-space-nowrap" v-if="data.updated_at">{{ formatDate(data.updated_at) }}</small>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.currency_code" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Payload валюта</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs uppercase" v-if="data.payload && data.payload.currency_code">{{ data.payload.currency_code }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.bank_code" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Payload банк</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.bank_code">{{ data.payload.bank_code }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.payment_system_code" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Payload плат. система</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.payment_system_code">{{ data.payload.payment_system_code }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.fullname" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Имя</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.fields && data.payload.fields.fullname">{{ data.payload.fields.fullname }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.phone_number" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Телефон</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.fields && data.payload.fields.phone_number">{{ data.payload.fields.phone_number }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.telegram" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Telegram</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 align-items-center" v-if="data.payload && data.payload.fields && data.payload.fields.telegram">
                    <span class="text-xs">{{ data.payload.fields.telegram }}</span>
                    <Button icon="pi pi-copy" @click="copy(data.payload.fields.telegram)" size="small" class="border-round-md border-round-lg border-0" severity="info" outlined/>
                </div>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.card_number" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Карта</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.fields && data.payload.fields.card_number">{{ data.payload.fields.card_number }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.account_number" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Счет</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.fields && data.payload.fields.account_number">{{ data.payload.fields.account_number }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.iban_number" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">IBAN</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.fields && data.payload.fields.iban_number">{{ data.payload.fields.iban_number }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.swift_code" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">SWIFT</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.fields && data.payload.fields.swift_code">{{ data.payload.fields.swift_code }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.wallet_address" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Кошелек</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-14rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.fields && data.payload.fields.wallet_address">{{ data.payload.fields.wallet_address }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.wallet_network" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Сеть</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs uppercase" v-if="data.payload && data.payload.fields && data.payload.fields.wallet_network">{{ data.payload.fields.wallet_network }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.upi_id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">UPI ID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.fields && data.payload.fields.upi_id">{{ data.payload.fields.upi_id }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.bank_name" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Банк (имя)</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.fields && data.payload.fields.bank_name">{{ data.payload.fields.bank_name }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.address" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Адрес</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-16rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.fields && data.payload.fields.address">{{ data.payload.fields.address }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.city" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Город</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.fields && data.payload.fields.city">{{ data.payload.fields.city }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.country" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Страна</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.fields && data.payload.fields.country">{{ data.payload.fields.country }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <Column field="payload.fields.wallet_id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Wallet ID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payload && data.payload.fields && data.payload.fields.wallet_id">{{ data.payload.fields.wallet_id }}</span>
                <span class="text-xs text-500" v-else>-</span>
            </template>
        </Column>

        <template #footer>
            <span class="text-sm">
                Всего {{ totalRecords }} получателей в блэклисте.
            </span>
        </template>
    </DataTable>

    <Skeleton height="2rem" v-else />

    <Dialog
        v-model:visible="entryDialogVisible"
        modal
        class="border-0"
        :breakpoints="{'960px': '85vw', '640px': '96vw'}"
        :style="{ width: '70vw' }"
        @hide="closeEntryDialog"
    >
        <template #header>
            <div class="flex flex-column gap-1">
                <span class="text-lg">{{ dialogMode === 'edit' ? 'Редактировать запись блэклиста' : 'Добавить запись в блэклист' }}</span>
                <span class="text-xs text-600" v-if="dialogMode === 'edit' && dialogEntry && (dialogEntry.id || dialogEntry._id)">
                    ID: {{ dialogEntry.id || dialogEntry._id }}
                </span>
            </div>
        </template>

        <RecipientsBlacklistForm
            v-if="entryDialogVisible"
            :mode="dialogMode"
            :initialEntry="dialogEntry"
            :saving="savingEntry"
            @cancel="closeEntryDialog"
            @submit="submitEntry"
        />
    </Dialog>
</template>

<script>
import Connect from '@/assets/js/Connect'
import Helper from '@/assets/js/Helper'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import RecipientsBlacklistForm from '@/components/admin/RecipientsBlacklistForm.vue'

const getDefaultFilters = () => ({
    id: { value: '', matchMode: 'contains' },
    _id: { value: '', matchMode: 'contains' },
    recipient_id: { value: '', matchMode: 'contains' },
    created_by: { value: '', matchMode: 'contains' },
    fingerprint: { value: '', matchMode: 'contains' },
    reason: { value: '', matchMode: 'contains' },
    source: { value: '', matchMode: 'contains' },
    active: { value: null, matchMode: FilterMatchMode.EQUALS },
    'payload.currency_code': { value: '', matchMode: 'contains' },
    'payload.bank_code': { value: '', matchMode: 'contains' },
    'payload.payment_system_code': { value: '', matchMode: 'contains' },
    'payload.fields.fullname': { value: '', matchMode: 'contains' },
    'payload.fields.phone_number': { value: '', matchMode: 'contains' },
    'payload.fields.card_number': { value: '', matchMode: 'contains' },
    'payload.fields.account_number': { value: '', matchMode: 'contains' },
    'payload.fields.wallet_address': { value: '', matchMode: 'contains' },
    'payload.fields.wallet_network': { value: '', matchMode: 'contains' },
    'payload.fields.telegram': { value: '', matchMode: 'contains' },
    'payload.fields.bank_name': { value: '', matchMode: 'contains' },
    'payload.fields.address': { value: '', matchMode: 'contains' },
    'payload.fields.city': { value: '', matchMode: 'contains' },
    'payload.fields.country': { value: '', matchMode: 'contains' },
    'payload.fields.swift_code': { value: '', matchMode: 'contains' },
    'payload.fields.iban_number': { value: '', matchMode: 'contains' },
    'payload.fields.upi_id': { value: '', matchMode: 'contains' },
    'payload.fields.wallet_id': { value: '', matchMode: 'contains' },
    created_at: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
    },
    updated_at: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
    },
})

export default {
    name: 'RecipientsBlacklistList',
    components: {
        RecipientsBlacklistForm
    },
    data() {
        return {
            list: [],
            totalRecords: 0,
            loading: false,
            lazyParams: {},
            columns: [
                { name: 'Реквизиты', value: 'Реквизиты' },
            ],
            selectedColumns: [],
            filters: getDefaultFilters(),
            sort: {
                field: 'created_at',
                order: -1
            },
            entryDialogVisible: false,
            dialogMode: 'add',
            dialogEntry: {},
            savingEntry: false,
            routeDialogHandled: false,
        }
    },
    methods: {
        get_column(val) {
            for (const [, column] of Object.entries(this.selectedColumns)) {
                if (column.name == val || column.value == val) return true
            }
            return false
        },
        copy(text) {
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: text, life: 1000 })
        },
        formatDate(value) {
            return this.helper.format_date(value)
        },
        detail_page(id, page, new_tab = false) {
            if (new_tab) {
                const routeData = this.$router.resolve({ path: '/admin/' + page + '/' + id })
                window.open(routeData.href, '_blank')
                return
            }
            this.$router.push({ path: '/admin/' + page + '/' + id })
        },
        openRecipientsById(recipientId) {
            if (!recipientId) return
            const routeData = this.$router.resolve({
                path: '/admin/recipients/',
                query: { id: recipientId }
            })
            window.open(routeData.href, '_blank')
        },
        openAddDialog(prefill = null) {
            this.dialogMode = 'add'
            this.dialogEntry = prefill ? { ...prefill } : {}
            this.entryDialogVisible = true
        },
        openEditDialog(entry) {
            if (!entry) return
            this.dialogMode = 'edit'
            this.dialogEntry = { ...entry }
            this.entryDialogVisible = true
        },
        closeEntryDialog() {
            this.entryDialogVisible = false
            this.dialogEntry = {}
            this.savingEntry = false
            if (this.$route.path === '/admin/recipients/blacklist/add/') {
                this.$router.replace({ path: '/admin/recipients/blacklist/' })
            }
        },
        parsePrefillQuery() {
            const query = this.$route.query || {}
            return {
                recipient_id: query.recipient_id ? String(query.recipient_id) : '',
                reason: query.reason ? String(query.reason) : '',
                source: query.source ? String(query.source) : 'admin',
                active: query.active === 'false' ? false : true,
            }
        },
        maybeOpenDialogFromRoute() {
            const shouldOpen = this.$route.path === '/admin/recipients/blacklist/add/' || this.$route.query.mode === 'add' || this.$route.query.recipient_id
            if (!shouldOpen || this.routeDialogHandled) return
            const prefill = this.parsePrefillQuery()
            this.routeDialogHandled = true
            this.openAddDialog(prefill)
        },
        async submitEntry(payload) {
            this.savingEntry = true
            let res = null

            if (this.dialogMode === 'edit') {
                res = await this.connect.AdminRecipientBlacklistEdit(payload.id, payload.data).then((response) => response)
            } else {
                res = await this.connect.AdminRecipientBlacklistAdd(payload).then((response) => response)
            }

            this.savingEntry = false

            if (!res || !res.result) {
                this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: res && res.message ? res.message : 'Не удалось сохранить запись', life: 8000 })
                return
            }

            this.$toast.add({ severity: 'success', summary: 'Успешно', detail: this.dialogMode === 'edit' ? 'Запись обновлена' : 'Запись добавлена', life: 4000 })
            this.closeEntryDialog()
            this.LoadRecipientsBlacklist()
        },
        onPage(event) {
            this.lazyParams = event
            this.lazyParams.sort = this.sort
            this.LoadRecipientsBlacklist()
        },
        onSort(event) {
            if (event.sortField && event.sortOrder) {
                this.sort = {
                    field: event.sortField,
                    order: event.sortOrder
                }
            } else {
                this.sort = {
                    field: 'created_at',
                    order: -1
                }
            }
            this.lazyParams.sort = this.sort
            this.LoadRecipientsBlacklist()
        },
        onFilter() {
            this.lazyParams.filters = this.filters
            this.LoadRecipientsBlacklist()
        },
        async LoadRecipientsBlacklist() {
            this.loading = true
            window.scrollTo({ top: 0, behavior: 'smooth' })
            const res = await this.connect.AdminRecipientsBlacklistList(
                this.lazyParams.rows,
                this.lazyParams.first,
                this.lazyParams.filters,
                this.lazyParams.sort
            ).then(res => res)

            if (!res.result || !res.data) {
                this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Ошибка загрузки данных', life: 10000 })
                this.loading = false
                return
            }

            this.list = res.data
            this.totalRecords = res.total
            this.loading = false
        },
        resetFilter() {
            this.filters = getDefaultFilters()
            this.lazyParams = {
                first: 0,
                rows: 20,
                sortField: null,
                sortOrder: null,
                filters: this.filters,
                sort: this.sort,
            }
            this.LoadRecipientsBlacklist()
        },
    },
    async beforeCreate() {
        this.connect = new Connect()
        this.helper = new Helper()
    },
    watch: {
        '$route.fullPath': function () {
            if (this.$route.path === '/admin/recipients/blacklist/add/') {
                this.routeDialogHandled = false
                this.maybeOpenDialogFromRoute()
            }
        }
    },
    mounted() {
        const blacklist_id = this.$route.query.id ? this.$route.query.id : null
        if (blacklist_id) this.filters.id = { value: blacklist_id, matchMode: 'contains' }

        const recipient_id = this.$route.query.recipient_id ? this.$route.query.recipient_id : null
        if (recipient_id) this.filters.recipient_id = { value: recipient_id, matchMode: 'contains' }

        this.lazyParams = {
            first: 0,
            rows: 20,
            sortField: null,
            sortOrder: null,
            filters: this.filters,
            sort: this.sort,
        }
        this.LoadRecipientsBlacklist()
        this.maybeOpenDialogFromRoute()
    }
}
</script>